import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import SellIcon from "@mui/icons-material/Sell";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import HomeIcon from "@mui/icons-material/Home";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HandymanIcon from "@mui/icons-material/Handyman";
import { FaCrown } from "react-icons/fa";
import SearchIcon from "@mui/icons-material/Search";
import HandshakeIcon from "@mui/icons-material/Handshake";
import StarIcon from "@mui/icons-material/Star";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Logout from "@mui/icons-material/Logout";

import { useNavigate } from "react-router-dom";
import { BASE_URL } from "./../../Api/ApiUrls";
import { AuthContext } from "./../../AuthContext/AuthContext";
import GuestLoginModal from "../../GuestLoginModal";

const WorkerDetail = () => {
  const navigate = useNavigate();

  const { userId, isGuest, logout } = useContext(AuthContext);

  const [showGuestModal, setShowGuestModal] = useState(false);
  const [userData, setUserData] = useState(null);

  const [propertyCounts, setPropertyCounts] = useState({
    sellProperty: 0,
    rentProperty: 0,
    leaseProperty: 0,
    jvbuilt: 0,
    bankAuction: 0,
    wanted: 0,
    newLaunching: 0,
    vendors: 0,
  });

  // ----------------------------------------------------
  // FETCH USER DATA
  // ----------------------------------------------------

  useEffect(() => {
    if (!userId || isGuest) return;

    fetch(`${BASE_URL}/users/${userId}/`)
      .then((response) => response.json())
      .then((data) => {
        setUserData(data);
      })
      .catch((error) => console.error("Error fetching user data:", error));
  }, [userId, isGuest]);

  // ----------------------------------------------------
  // PROTECTED NAVIGATION
  // ----------------------------------------------------

  const handleProtectedNavigation = (path) => {
    if (isGuest) {
      setShowGuestModal(true);
    } else {
      navigate(path);
    }
  };

  // ----------------------------------------------------
  // LOGOUT HANDLER
  // ----------------------------------------------------

  const handleLogout = async () => {
    console.log('🔄 Logout process initiated');

    try {
      const userId = localStorage.getItem('user_id');

      if (!userId) {
        console.warn('⚠️ No user_id found in localStorage, proceeding with client-side logout only');
      } else {
        console.log(`📤 Sending logout request for user_id: ${userId}`);

        const response = await fetch(`${BASE_URL}/logout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user_id: userId })
        });

        if (response.ok) {
          console.log('✅ Logout API call successful - User session terminated on server');
        } else {
          console.warn(`⚠️ Logout API returned status: ${response.status} - ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error('❌ Logout API error:', error.message);
      console.log('🔄 Continuing with client-side logout despite API error');
    } finally {
      console.log('🧹 Cleaning up client-side authentication data');
      logout();
      console.log('🚀 Redirecting to home page');
      navigate("/");
      console.log('🎉 Logout process completed successfully');
    }
  };

  // ----------------------------------------------------
  // FETCH PROPERTY COUNTS
  // ----------------------------------------------------

  useEffect(() => {
    if (!userId) return;

    const counts = {
      sellProperty: 0,
      rentProperty: 0,
      leaseProperty: 0,
      jvbuilt: 0,
      bankAuction: 0,
      wanted: 0,
      newLaunching: 0,
      vendors: 0,
    };

    // ------------------------------------------
    // SELL / RENT / LEASE COUNTS
    // ------------------------------------------

    const propertyTypeFetches = [
      fetch(`${BASE_URL}/get-property/${userId}/sell/`)
        .then((r) => r.json())
        .then((data) => {
          counts.sellProperty = data?.count ?? 0;
        }),

      fetch(`${BASE_URL}/get-property/${userId}/rent/`)
        .then((r) => r.json())
        .then((data) => {
          counts.rentProperty = data?.count ?? 0;
        }),

      fetch(`${BASE_URL}/get-property/${userId}/lease/`)
        .then((r) => r.json())
        .then((data) => {
          counts.leaseProperty = data?.count ?? 0;
        }),
    ];

    // ------------------------------------------
    // JV / JD + BUILD TO SUIT
    // ------------------------------------------

    const jvbuiltFetch = Promise.all([
      fetch(`${BASE_URL}/properties/filter/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          include: {
            user_id: Number(userId),
            type: "jv/jd",
          },
          exclude: {},
        }),
      }).then((r) => r.json()),

      fetch(`${BASE_URL}/properties/filter/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          include: {
            user_id: Number(userId),
            type: "build to suit",
          },
          exclude: {},
        }),
      }).then((r) => r.json()),
    ]).then(([jvjdData, buildToSuitData]) => {
      counts.jvbuilt =
        (jvjdData?.count || 0) +
        (buildToSuitData?.count || 0);
    });

    // ------------------------------------------
    // VENDOR COUNT
    // ------------------------------------------

    const vendorsFetch = fetch(`${BASE_URL}/vendors/`)
      .then((r) => r.json())
      .then((vendorData) => {
        counts.vendors = Array.isArray(vendorData)
          ? vendorData.filter(
              (vendor) => vendor.user_id == userId
            ).length
          : 0;
      });

    // ------------------------------------------
    // WANTED PROPERTY COUNT
    // ------------------------------------------

    const wantedFetch = fetch(
      `${BASE_URL}/property-request/?user_id=${userId}`
    )
      .then((r) => r.json())
      .then((wantedData) => {
        counts.wanted =
          wantedData?.pagination?.total_count ?? 0;
      });

    // ------------------------------------------
    // BANK AUCTION COUNT
    // ------------------------------------------

    const auctionFetch = fetch(
      `${BASE_URL}/bank-properties/filter/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          include: {
            user_id: Number(userId),
          },
          exclude: {},
        }),
      }
    )
      .then((r) => r.json())
      .then((auctionData) => {
        counts.bankAuction = auctionData?.count || 0;
      });

      // ------------------------------------------
// NEW LAUNCHING / PREMIUM / FUTURE LAUNCHING COUNT
// ------------------------------------------

const newLaunchingFetch = fetch(
  `https://landnest.net:81/ad-properties/performance/?user_id=${userId}`
)
  .then((r) => r.json())
  .then((data) => {
    counts.newLaunching = data?.count ?? 0;
  });

    // ------------------------------------------
    // UPDATE ALL COUNTS
    // ------------------------------------------

    Promise.all([
      ...propertyTypeFetches,
      jvbuiltFetch,
      vendorsFetch,
      wantedFetch,
      auctionFetch,
      newLaunchingFetch,
    ])
      .then(() => {
        console.log("Final property counts:", counts);
        setPropertyCounts({
          ...counts,
        });
      })
      .catch((error) => {
        console.error(
          "Error fetching property counts:",
          error
        );
      });
  }, [userId]);

  // ----------------------------------------------------
  // MENU ITEMS
  // ----------------------------------------------------

  const menuItems = [
    {
      text: "Profile",
      icon: <PersonIcon />,
      iconColor: "#8b4b22",
      action: () =>
        navigate(`/user-profile/${userId}`),
    },

    {
      text: "Subscription Plans",
       icon: <FaCrown size={20} />,

      iconColor: "#d09a28",
      action: () =>
        navigate("/user-subscription"),
    },

    {
      text: "Sell Properties",
      icon: <SellIcon />,
      iconColor: "#965020",
      count: propertyCounts.sellProperty,
      action: () =>
        handleProtectedNavigation("/user-buy"),
    },

    {
      text: "Rent Properties",
      icon: <LocalOfferIcon />,
      iconColor: "#d2a52e",
      count: propertyCounts.rentProperty,
      action: () =>
        handleProtectedNavigation("/user-rent"),
    },

    {
      text: "Lease Properties",
      icon: <HomeIcon />,
      iconColor: "#3d88c6",
      count: propertyCounts.leaseProperty,
      action: () =>
        handleProtectedNavigation("/user-lease"),
    },

    {
      text: "JD/ JV Built to suit properties",
      icon: <ApartmentIcon />,
      iconColor: "#8461c7",
      count: propertyCounts.jvbuilt,
      action: () =>
        handleProtectedNavigation(
          "/builtSuitProperties"
        ),
    },

    {
      text: "Auction Properties",
      icon: <AccountCircleIcon  />,
      iconColor: "#ed8a20",
      count: propertyCounts.bankAuction,
      action: () =>
        handleProtectedNavigation(
          "/user-bankauction"
        ),
    },

    {
      text: "Wanted Properties",
      icon: <SearchIcon />,
      iconColor: "#3a9d5d",
      count: propertyCounts.wanted,
      action: () =>
        handleProtectedNavigation(
          "/user-wanted"
        ),
    },

    {
      text:
        "New Launching/ Premium/Future Launching Properties",
      icon: <HandymanIcon  />,
      iconColor: "#bd2525",
      count: propertyCounts.newLaunching,
      action: () =>
        handleProtectedNavigation(
          "/new-launching-properties"
        ),
    },

    {
      text: "Vendor Registration",
      icon: <HandshakeIcon />,
      iconColor: "#d09d32",
      count: propertyCounts.vendors,
      action: () =>
        handleProtectedNavigation(
          "/user-vendors"
        ),
    },
  ];

  // ----------------------------------------------------
  // FORMAT COUNT
  // Example: 1 -> 01
  // ----------------------------------------------------

  const formatCount = (count) => {
    return String(count ?? 0).padStart(2, "0");
  };

  // ----------------------------------------------------
  // UI
  // ----------------------------------------------------

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#f6f2e9",
        color: "#222",
        overflowX: "hidden",
      }}
    >

      {/* =================================================
          HEADER / APP BAR
      ================================================= */}

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: `
            linear-gradient(145deg, rgb(22, 22, 22), rgb(15, 15, 15)),
            url('/construction5.png')
          `,
          color: 'text.primary',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            edge="start"
            onClick={() => navigate(-1)}
            sx={{
              color: '#ffffff',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
            borderRadius: '16px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)'
            }
          }}>
            <Avatar
              alt={userData?.username || "User"}
              src={userData?.profile ? `${BASE_URL}${userData.profile}` : ""}
              sx={{
                width: 36,
                height: 36,
                mr: 1.5,
                border: '2px solid rgba(255,255,255,0.3)'
              }}
            />
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ffffff' }}>
                {isGuest
                  ? "Guest"
                  : userData?.username
                    ? userData.username
                        .split(" ")
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(" ")
                    : "Loading..."}
              </Typography>

              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
                {isGuest
                  ? "GUEST USER"
                  : userData?.user_id
                    ? `CUSTOMER${userData.user_id.toString().padStart(4, '0')}`
                    : "Loading..."}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Spacer for fixed header */}
      <Box sx={{ height: '64px' }} />

      {/* =================================================
          MAIN MENU
      ================================================= */}

      <Container
        maxWidth="sm"
        disableGutters
        sx={{
          minHeight: "100vh",
          px: {
            xs: 1.5,
            sm: 2,
          },
          pt: 1.25,
          // Space for bottom fixed buttons
          pb: "92px",
        }}
      >

        <List
          disablePadding
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "7px",
          }}
        >

          {menuItems.map((item) => (
            <Card
              key={item.text}
              elevation={0}
              sx={{
                width: "100%",
                borderRadius: "10px",
                background: "#F8F7F4",  // <--- CHANGED HERE
                border:
                  "1px solid #e7e1d7",
                boxShadow:
                  "0 2px 7px rgba(77, 57, 34, 0.10)",
                overflow: "hidden",
              }}
            >

         <ListItemButton
  onClick={item.action}
  sx={{
    minHeight: {
      xs: 46,
      sm: 46, 
    },
    height: "auto",  
    px: {
      xs: 1.33,
      sm: 1.7,
    },
    py: 1.4,  // Increased padding
    borderRadius: "10px",
    "&:hover": {
      backgroundColor: "#faf8f4",
    },
    "&:active": {
      backgroundColor: "#f5f0e8",
    },
  }}
>

                {/* -------------------------------
                    ICON
                -------------------------------- */}

                <ListItemIcon
                  sx={{
                    minWidth: {
                      xs: 35,
                      sm: 39,
                    },
                    color: item.iconColor,
                    display: "flex",
                    alignItems: "center",
                    "& svg": {
                      fontSize: {
                        xs: 21,
                        sm: 23,
                      },
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                {/* -------------------------------
                    TEXT
                -------------------------------- */}

             <ListItemText
  primary={item.text}
  sx={{
    my: 0,
    mr: 0.8,
    minWidth: 0,
    flex: 1,
  }}
  primaryTypographyProps={{
    sx: {
      fontSize: {
        xs: "14px",
        sm: "12px",
      },
      fontWeight: 600,
      color: "#222",
      lineHeight: 1.2,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      wordBreak: "break-word",
    },
  }}
/>

                {/* -------------------------------
                    COUNT BADGE
                -------------------------------- */}

                {item.count !== undefined && (
                  <Box
                    sx={{
                      flexShrink: 0,
                      minWidth: 27,
                      height: 20,
                      px: 0.75,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        "center",
                      backgroundColor:
                        "#9a4f20",
                      color: "#fff",
                      fontSize: "10px",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {formatCount(
                      item.count
                    )}
                  </Box>
                )}

                {/* -------------------------------
                    RIGHT ARROW
                -------------------------------- */}

                <ChevronRightIcon
                  sx={{
                    flexShrink: 0,
                    ml: 0.65,
                    fontSize: 20,
                    color: "#666",
                  }}
                />

              </ListItemButton>

            </Card>
          ))}

          {/* =============================================
              LOGOUT BUTTON
          ============================================= */}
          <Box>
            <Typography
              onClick={handleLogout}
              sx={{
                fontSize: {
                  xs: "13px",
                  sm: "14px",
                },
                fontWeight: 600,
                color: "#9a4f20",
                cursor: "pointer",
                mt: 1.5,
                display: "flex",
                justifyContent: "start",
                "&:hover": {
                  textDecoration: "underline",
                  color: "#7a3f18",
                },
              }}
            >
              Logout
            </Typography>
          </Box>

        </List>

      </Container>

      {/* =================================================
          BOTTOM ACTION BAR
      ================================================= */}

      <Box
        sx={{
          position: "fixed",
          zIndex: 20,
          left: 0,
          right: 0,
          bottom: 0,
          px: {
            xs: 1.5,
            sm: 2,
          },
          pb: {
            xs: 2,
            sm: 2.2,
          },
          pt: 0.8,
          background:
            "linear-gradient(to top, #f6f2e9 70%, transparent)",
             display: "flex",       
    justifyContent: "center", 
        }}
      >

        <Box
          sx={{
            width: "100%",
            maxWidth: 560,
            mx: "auto",
            p: 0.9,
            display: "flex",
            gap: 0.75,
            borderRadius: "12px",
            background:
              "rgba(255,255,255,0.98)",
            border:
              "1px solid #e6dfd4",
            boxShadow:
              "0 4px 16px rgba(70, 52, 32, 0.14)",
          }}
        >

          {/* ==========================================
              POST PROPERTY
          ========================================== */}

          <Button
            onClick={() =>
              handleProtectedNavigation(
                "/post"
              )
            }
            startIcon={
              <AddCircleOutlineIcon />
            }
            sx={{
              flex: 1.25,
              minWidth: 0,
              height: 40,
              px: 0.8,
              borderRadius: "7px",
              backgroundColor:
                "#9a4f20",
              color: "#fff",
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 500,
              whiteSpace: "nowrap",
              "& .MuiButton-startIcon": {
                marginRight: "5px",
                marginLeft: 0,
              },
              "& svg": {
                fontSize: 21,
              },
              "&:hover": {
                backgroundColor:
                  "#864319",
              },
            }}
          >
            Post Property
          </Button>

          {/* ==========================================
              SUBSCRIPTION
          ========================================== */}

          <Button
            onClick={() =>
              navigate(
                "/user-subscription"
              )
            }
            startIcon={
             <StarIcon />
            }
            sx={{
              flex: 1,
              minWidth: 0,
              height: 40,
              px: 0.6,
              borderRadius: "7px",
              border:
                "1px solid #d8cfc2",
              backgroundColor: "#fff",
              color: "#8b572f",
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              "& .MuiButton-startIcon": {
                marginRight: "4px",
                marginLeft: 0,
              },
              "& svg": {
                fontSize: 20,
                color: "#d2a52e",
              },
              "&:hover": {
                backgroundColor:
                  "#fbf8f2",
              },
            }}
          >
            Subscription
          </Button>

          {/* ==========================================
              SUPPORT
          ========================================== */}

          <Button
            onClick={() =>
              navigate("/support")
            }
            startIcon={
              <SupportAgentIcon />
            }
            sx={{
              flex: 0.95,
              minWidth: 0,
              height: 40,
              px: 0.5,
              borderRadius: "7px",
              border:
                "1px solid #d8cfc2",
              backgroundColor: "#fff",
              color: "#8b572f",
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              "& .MuiButton-startIcon": {
                marginRight: "4px",
                marginLeft: 0,
              },
              "& svg": {
                fontSize: 20,
              },
              "&:hover": {
                backgroundColor:
                  "#fbf8f2",
              },
            }}
          >
            Support
          </Button>

        </Box>

      </Box>

      {/* =================================================
          GUEST LOGIN MODAL
      ================================================= */}

      <GuestLoginModal
        open={showGuestModal}
        onClose={() =>
          setShowGuestModal(false)
        }
      />

    </Box>
  );
};

export default WorkerDetail;